import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Truck, Save, X, Trash2, AlertCircle, ScrollText, CheckCircle, XCircle, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

const STATUS_FLOW = [
  ['awaiting_shipping_quote', 'Menunggu Quote Ongkir'],
  ['pending_payment',  'Menunggu Pembayaran'],
  ['payment_submitted','Bukti Terkirim — Menunggu Verifikasi'],
  ['payment_verified', 'Pembayaran Diverifikasi'],
  ['processing',       'Diproses'],
  ['packed',           'Dikemas'],
  ['shipped',          'Dikirim'],
  ['delivered',        'Diterima'],
  ['completed',        'Selesai'],
  ['cancelled',        'Dibatalkan'],
];

const ALLOWED = {
  awaiting_shipping_quote: ['cancelled'],
  pending_payment:  ['payment_submitted', 'payment_verified', 'cancelled'],
  payment_submitted:['payment_verified', 'pending_payment', 'cancelled'],
  payment_verified: ['processing', 'cancelled'],
  processing:       ['packed', 'cancelled'],
  packed:           ['shipped', 'cancelled'],
  shipped:          ['delivered', 'cancelled'],
  delivered:        ['completed'],
  completed:        [],
  cancelled:        [],
};

const DELETABLE_STATUSES = ['awaiting_shipping_quote', 'pending_payment', 'payment_submitted', 'cancelled'];

const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
const fmtIDR = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(Number(n) || 0);

/**
 * Inline admin panel embedded inside the Admin Orders detail row.
 * Props:
 *   order: current order doc
 *   onClose(): collapse panel
 *   onUpdated(updatedOrder): callback after successful save
 *   onDeleted(orderId): callback after successful delete
 */
export const AdminOrderManager = ({ order, onClose, onUpdated, onDeleted }) => {
  const [status, setStatus] = useState(order.status || 'pending_payment');
  const [courier, setCourier] = useState(order.tracking_courier || order.shipping_courier || '');
  const [service, setService] = useState(order.tracking_service || order.shipping_service || '');
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [notes, setNotes] = useState(order.tracking_notes || '');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Manual shipping quote state
  const [quoteCost, setQuoteCost] = useState('');
  const [quoteCourier, setQuoteCourier] = useState('JNE');
  const [quoteService, setQuoteService] = useState('REG');
  const [quoteNote, setQuoteNote] = useState('');
  const [quoting, setQuoting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setStatus(order.status || 'pending_payment');
    setCourier(order.tracking_courier || order.shipping_courier || '');
    setService(order.tracking_service || order.shipping_service || '');
    setTracking(order.tracking_number || '');
    setNotes(order.tracking_notes || '');
    setAdminNote('');
    setQuoteCost('');
    setQuoteCourier(order.shipping_courier || 'JNE');
    setQuoteService(order.shipping_service || 'REG');
    setQuoteNote('');
  }, [order.id, order.status, order.shipping_courier, order.shipping_service, order.tracking_courier, order.tracking_service, order.tracking_number, order.tracking_notes]);

  const allowedNext = ALLOWED[order.status || 'pending_payment'] || [];
  const currentStatus = order.status || 'pending_payment';
  const isAwaitingQuote = currentStatus === 'awaiting_shipping_quote';
  const isPaymentSubmitted = currentStatus === 'payment_submitted';
  const canDelete = DELETABLE_STATUSES.includes(currentStatus);

  const submitApproveReject = async (decision) => {
    // decision: 'approve' → status=payment_verified ; 'reject' → status=pending_payment
    const newStatus = decision === 'approve' ? 'payment_verified' : 'pending_payment';
    const note = decision === 'approve'
      ? 'Admin: pembayaran disetujui'
      : 'Admin: bukti transfer ditolak, mohon transfer ulang';
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.id}`,
        { status: newStatus, admin_note: note },
        { withCredentials: true }
      );
      toast.success(decision === 'approve' ? 'Pembayaran disetujui' : 'Bukti ditolak');
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memperbarui order');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (status !== (order.status || 'pending_payment')) payload.status = status;
      if (courier !== (order.tracking_courier || '')) payload.tracking_courier = courier;
      if (service !== (order.tracking_service || '')) payload.tracking_service = service;
      if (tracking !== (order.tracking_number || '')) payload.tracking_number = tracking;
      if (notes !== (order.tracking_notes || '')) payload.tracking_notes = notes;
      if (adminNote) payload.admin_note = adminNote;

      if (Object.keys(payload).length === 0) {
        toast.info('Tidak ada perubahan');
        setSaving(false);
        return;
      }

      if (payload.status === 'shipped' && !(payload.tracking_number || order.tracking_number)) {
        toast.error('Nomor resi wajib diisi sebelum pesanan dikirim.');
        setSaving(false);
        return;
      }

      const { data } = await axios.patch(`${API}/admin/orders/${order.id}`, payload, { withCredentials: true });
      toast.success('Order diperbarui');
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memperbarui order');
    } finally {
      setSaving(false);
    }
  };

  const submitQuote = async () => {
    const cost = parseInt(quoteCost, 10);
    if (!cost || cost <= 0) {
      toast.error('Masukkan ongkir dalam Rupiah (> 0)');
      return;
    }
    if (!quoteCourier.trim() || !quoteService.trim()) {
      toast.error('Pilih kurir dan service');
      return;
    }
    setQuoting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/orders/${order.id}/quote-shipping`,
        { shipping_cost: cost, courier: quoteCourier, service: quoteService, note: quoteNote },
        { withCredentials: true }
      );
      toast.success(`Ongkir Rp${cost.toLocaleString('id-ID')} disubmit. Customer akan diberi notifikasi.`);
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal submit quote');
    } finally {
      setQuoting(false);
    }
  };

  const submitDelete = async () => {
    if (!window.confirm(`Hapus order #${order.id.slice(0, 8)}? Tindakan ini tidak bisa dibatalkan.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/orders/${order.id}`, { withCredentials: true });
      toast.success('Order dihapus');
      onDeleted && onDeleted(order.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus order');
    } finally {
      setDeleting(false);
    }
  };

  const subtotal = Number(order.subtotal || 0);
  const discount = Number(order.discount || 0);
  const quotedTotal = Math.max(0, subtotal - discount + (parseInt(quoteCost || 0, 10) || 0));

  return (
    <div className="rounded-xl p-4 mt-2" style={cardStyle} data-testid={`admin-order-manager-${order.id}`}>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm font-medium flex items-center gap-2" style={{ color: '#F8FAF9' }}>
          <Truck className="w-4 h-4" style={{ color: '#D4AF37' }} /> Manage Order
        </p>
        <button onClick={onClose} className="p-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#A3B8AC' }} data-testid="admin-order-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* ============== PAYMENT PROOF VIEWER (payment_submitted) ============== */}
      {isPaymentSubmitted && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.35)' }} data-testid="admin-payment-proof-card">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="w-4 h-4" style={{ color: '#A855F7' }} />
            <p className="text-sm font-semibold" style={{ color: '#A855F7' }}>
              Bukti Transfer dari Customer ({(order.payment_method || '-').toUpperCase().replace('_', ' ')})
            </p>
          </div>
          {order.payment_proof_url ? (
            <a href={order.payment_proof_url} target="_blank" rel="noreferrer" className="block" data-testid="admin-proof-link">
              <img
                src={order.payment_proof_url}
                alt="Payment proof"
                className="max-h-72 w-auto mx-auto rounded-lg object-contain"
                style={{ background: 'rgba(255,255,255,0.05)' }}
                data-testid="admin-proof-img"
              />
            </a>
          ) : (
            <p className="text-xs italic" style={{ color: '#A3B8AC' }}>(No proof image — payment_method changed without upload)</p>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs" style={{ color: '#CFE0D5' }}>
            <div><span style={{ color: '#A3B8AC' }}>Customer: </span>{order.user_email || order.user_id?.slice(0, 8) || '-'}</div>
            <div><span style={{ color: '#A3B8AC' }}>Total: </span><span style={{ color: '#D4AF37', fontWeight: 600 }}>{fmtIDR(order.total_amount || 0)}</span></div>
            <div className="col-span-2"><span style={{ color: '#A3B8AC' }}>Submitted: </span>{order.payment_proof_submitted_at ? new Date(order.payment_proof_submitted_at).toLocaleString('id-ID') : '-'}</div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => submitApproveReject('approve')}
              disabled={saving}
              className="px-4 py-2.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: '#22C55E', color: '#080C0A' }}
              data-testid="admin-approve-payment-btn"
            >
              <CheckCircle className="w-4 h-4" /> Approve Payment
            </button>
            <button
              onClick={() => submitApproveReject('reject')}
              disabled={saving}
              className="px-4 py-2.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.45)', color: '#EF4444' }}
              data-testid="admin-reject-payment-btn"
            >
              <XCircle className="w-4 h-4" /> Reject Payment
            </button>
          </div>
        </div>
      )}

      {/* ============== MANUAL SHIPPING QUOTE FORM ============== */}
      {isAwaitingQuote && (
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)' }} data-testid="admin-quote-shipping-form">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
            <p className="text-sm font-semibold" style={{ color: '#F59E0B' }}>Tetapkan Ongkir Manual</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Ongkir (Rupiah) *</label>
              <input
                type="number"
                min="0"
                step="500"
                value={quoteCost}
                onChange={(e) => setQuoteCost(e.target.value)}
                placeholder="contoh: 25000"
                className="w-full rounded-lg px-3 py-2 outline-none text-sm"
                style={inputStyle}
                data-testid="quote-cost-input"
              />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Kurir *</label>
              <select
                value={quoteCourier}
                onChange={(e) => setQuoteCourier(e.target.value)}
                className="w-full rounded-lg px-3 py-2 outline-none text-sm"
                style={inputStyle}
                data-testid="quote-courier-input"
              >
                <option value="JNE">JNE</option>
                <option value="J&T">J&T</option>
                <option value="SiCepat">SiCepat</option>
                <option value="POS Indonesia">POS Indonesia</option>
                <option value="AnterAja">AnterAja</option>
                <option value="Tiki">Tiki</option>
                <option value="Gojek">Gojek (Instant)</option>
                <option value="Grab">Grab (Instant)</option>
                <option value="Lainnya">Lainnya</option>
              </select>
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Service *</label>
              <input
                value={quoteService}
                onChange={(e) => setQuoteService(e.target.value)}
                placeholder="REG / OKE / YES"
                className="w-full rounded-lg px-3 py-2 outline-none text-sm"
                style={inputStyle}
                data-testid="quote-service-input"
              />
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Catatan untuk Customer</label>
              <input
                value={quoteNote}
                onChange={(e) => setQuoteNote(e.target.value)}
                placeholder="Optional"
                className="w-full rounded-lg px-3 py-2 outline-none text-sm"
                style={inputStyle}
                data-testid="quote-note-input"
              />
            </div>
          </div>
          <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
            <div className="text-xs space-y-1" style={{ color: '#CFE0D5' }}>
              <div className="flex justify-between"><span>Subtotal</span><span>{fmtIDR(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between" style={{ color: '#D4AF37' }}><span>Diskon Tiket</span><span>-{fmtIDR(discount)}</span></div>}
              <div className="flex justify-between"><span>Ongkir (input)</span><span style={{ color: '#F59E0B' }}>{fmtIDR(parseInt(quoteCost, 10) || 0)}</span></div>
              <div className="flex justify-between font-semibold pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}>
                <span>Total Baru</span><span style={{ color: '#D4AF37' }}>{fmtIDR(quotedTotal)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={submitQuote}
            disabled={quoting || !quoteCost}
            className="mt-3 w-full px-4 py-2.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: '#F59E0B', color: '#080C0A' }}
            data-testid="submit-quote-btn"
          >
            {quoting ? 'Memproses…' : 'Apply Quote → Pending Payment'}
          </button>
        </div>
      )}

      {/* ============== STANDARD STATUS / TRACKING FORM ============== */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Change Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-lg px-3 py-2 outline-none text-sm"
            style={inputStyle}
            data-testid="admin-order-status-select"
          >
            {STATUS_FLOW.map(([k, label]) => {
              const disabled = k !== currentStatus && !allowedNext.includes(k);
              return (
                <option key={k} value={k} disabled={disabled}>
                  {label}{k === currentStatus ? ' (saat ini)' : ''}{disabled ? ' — tidak diizinkan' : ''}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Courier</label>
          <select
            value={courier}
            onChange={(e) => setCourier(e.target.value)}
            className="w-full rounded-lg px-3 py-2 outline-none text-sm"
            style={inputStyle}
            data-testid="admin-order-courier-input"
          >
            <option value="">Pilih Courier</option>
            <option value="JNE">JNE</option>
            <option value="J&T">J&T</option>
            <option value="SiCepat">SiCepat</option>
            <option value="POS Indonesia">POS Indonesia</option>
            <option value="AnterAja">AnterAja</option>
            <option value="Tiki">Tiki</option>
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Service</label>
          <input
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="REG / OKE / YES"
            className="w-full rounded-lg px-3 py-2 outline-none text-sm"
            style={inputStyle}
            data-testid="admin-order-service-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>
            Tracking Number (Resi) <span style={{ color: '#EF4444' }}>{status === 'shipped' ? '*' : ''}</span>
          </label>
          <input
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="contoh: JNE123456789"
            className="w-full rounded-lg px-3 py-2 outline-none text-sm font-mono"
            style={inputStyle}
            data-testid="admin-order-tracking-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Shipping Notes</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Catatan opsional untuk customer"
            className="w-full rounded-lg px-3 py-2 outline-none text-sm"
            style={inputStyle}
            data-testid="admin-order-notes-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs block mb-1" style={{ color: '#A3B8AC' }}>Admin internal note (akan masuk timeline)</label>
          <input
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Optional"
            className="w-full rounded-lg px-3 py-2 outline-none text-sm"
            style={inputStyle}
            data-testid="admin-order-admin-note-input"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {canDelete && (
          <button
            onClick={submitDelete}
            disabled={deleting}
            className="px-4 py-2 rounded-full font-semibold flex items-center gap-2 disabled:opacity-50 text-sm"
            style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.45)', color: '#EF4444' }}
            data-testid="admin-order-delete-btn"
          >
            <Trash2 className="w-4 h-4" /> {deleting ? 'Menghapus…' : 'Hapus Order'}
          </button>
        )}
        <button
          onClick={submit}
          disabled={saving}
          className="px-5 py-2 rounded-full font-semibold flex items-center gap-2 disabled:opacity-50"
          style={{ background: '#D4AF37', color: '#080C0A' }}
          data-testid="admin-order-save-btn"
        >
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>

      {Array.isArray(order.status_history) && order.status_history.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs mb-2 flex items-center gap-1" style={{ color: '#A3B8AC' }}>
            <ScrollText className="w-3 h-3" /> History:
          </p>
          <ul className="space-y-1 text-xs" style={{ color: '#CFE0D5' }}>
            {[...order.status_history].sort((a,b) => new Date(b.at) - new Date(a.at)).slice(0, 8).map((h, i) => (
              <li key={i}>
                <span style={{ color: '#D4AF37' }}>{h.status}</span> · {new Date(h.at).toLocaleString('id-ID')} · {h.by_email || h.by || ''} {h.note ? `· "${h.note}"` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
